import React, { useState } from 'react'
import { Pencil, MessageSquarePlus, Loader2 } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { TimelineCard } from '@/components/TimelineCard'
import { MilestoneSubmissionsCard } from '@/components/collaboration/MilestoneSubmissionsCard'
import { CollaborationDocuments } from '@/components/collaboration/CollaborationDocuments'
import { useToast } from '@/hooks/use-toast'
import {
  useUpdateMilestoneDueDateMutation,
  useUpdateOfferTermsMutation,
  useSendCollaborationMessageMutation,
} from '@/redux/marketerApi'

const PLATFORMS = ['TikTok', 'Instagram', 'YouTube', 'Facebook']
export const COLLABORATION_DETAIL_TABS = ['activity', 'submissions', 'files', 'campaign'] as const

export type CollaborationDetailsTab = (typeof COLLABORATION_DETAIL_TABS)[number]

interface CollaborationDetailsTabsProps {
  activeTab: CollaborationDetailsTab
  campaign: any
  collaboration: any
  campaignId: string | undefined
  collaborationId: string | undefined
  filteredTimeline: any[]
  onViewSubmission: (submissionId: string) => void
  onTabChange: (tab: CollaborationDetailsTab) => void
}

export const CollaborationDetailsTabs: React.FC<CollaborationDetailsTabsProps> = ({
  activeTab,
  campaign,
  collaboration,
  campaignId,
  collaborationId,
  filteredTimeline,
  onViewSubmission,
  onTabChange,
}) => {
  const { toast } = useToast()
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [composing, setComposing] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [sendCollaborationMessage, { isLoading: isSendingMessage }] =
    useSendCollaborationMessageMutation()

  const handleSendMessage = async () => {
    if (!messageText.trim() || !collaborationId) return
    try {
      await sendCollaborationMessage({
        collaborationId,
        message: messageText.trim(),
      }).unwrap()
      setMessageText('')
      setComposing(false)
      toast({ title: 'Message sent', description: 'Your message has been added to the timeline.' })
    } catch {
      toast({ title: 'Failed to send', description: 'Please try again.', variant: 'destructive' })
    }
  }
  const effectivePlatforms: string[] = (() => {
    const p = collaboration.platform ?? campaign?.platform
    if (Array.isArray(p) && p.length > 0) return p
    return []
  })()
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(effectivePlatforms)
  const [updateOfferTerms, { isLoading: isSavingPlatform }] = useUpdateOfferTermsMutation()
  const [updateMilestoneDueDate, { isLoading: isSavingMilestoneDueDate }] =
    useUpdateMilestoneDueDateMutation()

  const togglePlatform = (p: string) => {
    setSelectedPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]))
  }

  const platformsChanged =
    JSON.stringify([...selectedPlatforms].sort()) !==
    JSON.stringify([...effectivePlatforms].sort())

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false)
  const [editPayment, setEditPayment] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null)
  const [editingMilestoneDueDate, setEditingMilestoneDueDate] = useState('')

  const startEdit = () => {
    setEditPayment(collaboration.paymentAmount != null ? String(collaboration.paymentAmount) : '')
    setEditDueDate(collaboration.contentDueDate ? collaboration.contentDueDate.split('T')[0] : '')
    setIsEditing(true)
  }

  const handleSaveEdit = async () => {
    if (!collaborationId) return
    const payload: { paymentAmount?: number; contentDueDate?: string } = {}
    if (editPayment.trim() !== '') {
      const parsed = parseFloat(editPayment)
      if (!Number.isNaN(parsed)) payload.paymentAmount = parsed
    }
    if (editDueDate.trim() !== '') payload.contentDueDate = editDueDate
    await updateOfferTerms({ id: collaborationId, data: payload })
    setIsEditing(false)
  }

  const handleSavePlatforms = async () => {
    if (!collaborationId || !platformsChanged) return
    await updateOfferTerms({ id: collaborationId, data: { platform: selectedPlatforms } })
  }

  const startMilestoneEdit = (milestone: any) => {
    setEditingMilestoneId(milestone.id)
    setEditingMilestoneDueDate(milestone.dueDate ? milestone.dueDate.split('T')[0] : '')
  }

  const cancelMilestoneEdit = () => {
    setEditingMilestoneId(null)
    setEditingMilestoneDueDate('')
  }

  const saveMilestoneDueDate = async (milestone: any) => {
    if (!collaborationId) return

    try {
      await updateMilestoneDueDate({
        collaborationId,
        milestoneId: milestone.id,
        dueDate: editingMilestoneDueDate || null,
      }).unwrap()

      toast({
        title: 'Milestone due date updated',
        description: `${milestone.name} was updated successfully.`,
      })
      cancelMilestoneEdit()
    } catch (error) {
      console.error('Failed to update milestone due date', error)
      toast({
        title: 'Update failed',
        description: 'Could not update milestone due date. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null
    const dateStr = dateString.split('T')[0]
    const [year, month, day] = dateStr.split('-')
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString(
      'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' },
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => onTabChange(value as CollaborationDetailsTab)}
      className="w-full"
    >
      <TabsList className="mb-4 flex h-auto w-full flex-wrap gap-1 rounded-2xl border border-border bg-card p-1.5 shadow-sm">
        <TabsTrigger
          value="campaign"
          className="h-10 flex-1 rounded-xl px-4 text-sm font-semibold text-muted-foreground data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm"
        >
          Terms & dates
        </TabsTrigger>
        <TabsTrigger
          value="files"
          className="h-10 flex-1 rounded-xl px-4 text-sm font-semibold text-muted-foreground data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm"
        >
          Files
        </TabsTrigger>
        <TabsTrigger
          value="activity"
          className="h-10 flex-1 rounded-xl px-4 text-sm font-semibold text-muted-foreground data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm"
        >
          Activity
        </TabsTrigger>
        <TabsTrigger
          value="submissions"
          className="h-10 flex-1 rounded-xl px-4 text-sm font-semibold text-muted-foreground data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm"
        >
          Submissions
        </TabsTrigger>
      </TabsList>

      <TabsContent value="activity" className="space-y-4">
        <TimelineCard timeline={filteredTimeline} onViewSubmission={onViewSubmission} />

        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          {composing ? (
            <div className="space-y-3">
              <Textarea
                placeholder="Write a message to the athlete..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={3}
                autoFocus
                className="bg-background"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="dark-outline"
                  onClick={() => {
                    setComposing(false)
                    setMessageText('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="lime"
                  disabled={isSendingMessage || !messageText.trim()}
                  onClick={handleSendMessage}
                >
                  {isSendingMessage ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="lime" className="w-full" onClick={() => setComposing(true)}>
              <MessageSquarePlus className="mr-2 h-4 w-4" />
              Message Athlete
            </Button>
          )}
        </div>
      </TabsContent>

      <TabsContent value="submissions">
        <MilestoneSubmissionsCard
          milestones={collaboration.milestones}
          campaignId={campaignId}
          collaborationId={collaborationId}
          onViewSubmission={onViewSubmission}
          postLinks={collaboration.postLinks}
        />
      </TabsContent>

      <TabsContent value="files">
        {collaborationId && (
          <CollaborationDocuments
            collaborationId={collaborationId}
            isAccepted={['accepted', 'in_progress', 'completed'].includes(collaboration.status)}
          />
        )}
      </TabsContent>

      <TabsContent value="campaign">
        <div className="space-y-6 rounded-2xl border border-border bg-card p-5 shadow-sm">
          {/* Campaign Basics */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Terms
              </h4>
              {!isEditing ? (
                <Button size="sm" variant="lime" className="h-7 text-xs" onClick={startEdit}>
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    className="h-7 text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="lime"
                    className="h-7 text-xs"
                    onClick={handleSaveEdit}
                    disabled={isSavingPlatform}
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Campaign Name</p>
                <p className="text-sm text-foreground">{campaign?.title}</p>
              </div>
              {(campaign?.startDate || campaign?.endDate) && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Campaign Dates</p>
                  <p className="text-sm text-foreground">
                    {formatDate(campaign?.startDate) ?? '—'} to{' '}
                    {formatDate(campaign?.endDate) ?? '—'}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-muted-foreground">Content Due Date</p>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    className="mt-1 h-8 text-sm w-44"
                  />
                ) : (
                  <p className="text-sm text-foreground">
                    {formatDate(collaboration.contentDueDate) ?? (
                      <span className="text-muted-foreground">Not set</span>
                    )}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Payment</p>
                {isEditing ? (
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Amount (USD)"
                    value={editPayment}
                    onChange={(e) => setEditPayment(e.target.value)}
                    className="mt-1 h-8 text-sm w-36"
                  />
                ) : (
                  <p className="text-sm text-foreground">
                    {collaboration.paymentAmount
                      ? formatCurrency(collaboration.paymentAmount)
                      : 'TBD'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Social Platform</p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => togglePlatform(p)}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        selectedPlatforms.includes(p)
                          ? 'bg-[#C0FD72] text-black border-[#C0FD72]'
                          : 'border-border bg-card text-foreground hover:bg-muted'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  {platformsChanged && (
                    <Button
                      size="sm"
                      onClick={handleSavePlatforms}
                      disabled={isSavingPlatform}
                      variant="lime"
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Milestone Due Dates */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Milestone Due Dates
            </h4>
            <div className="space-y-3">
              {(collaboration?.milestones || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No milestones found.</p>
              ) : (
                (collaboration?.milestones || []).map((milestone: any) => {
                  const isEditingMilestone = editingMilestoneId === milestone.id

                  return (
                    <div
                      key={milestone.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">{milestone.name}</p>
                        {!isEditingMilestone ? (
                          <p className="text-sm text-muted-foreground">
                            {formatDate(milestone.dueDate) ?? (
                              <span className="text-muted-foreground">Not set</span>
                            )}
                          </p>
                        ) : (
                          <Input
                            type="date"
                            value={editingMilestoneDueDate}
                            onChange={(e) => setEditingMilestoneDueDate(e.target.value)}
                            className="mt-1 h-8 text-sm w-44"
                          />
                        )}
                      </div>

                      {!isEditingMilestone ? (
                        <Button
                          size="sm"
                          onClick={() => startMilestoneEdit(milestone)}
                          variant="lime"
                          className="h-8"
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelMilestoneEdit}
                            className="h-8"
                            disabled={isSavingMilestoneDueDate}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => saveMilestoneDueDate(milestone)}
                            variant="lime"
                            className="h-8"
                            disabled={isSavingMilestoneDueDate}
                          >
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>

          {/* Creative Brief */}
          {campaign?.brief && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Creative Brief
              </h4>
              <p className="text-sm text-foreground">{campaign.brief}</p>
            </div>
          )}

          {/* Instructions */}
          {campaign?.instructions && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Instructions
              </h4>
              <p className="text-sm text-foreground">{campaign.instructions}</p>
            </div>
          )}

          {/* Licensing extension history — the "Extend License" action itself lives in the
              always-visible Quick Actions row on CollaborationDetails, since this tab doesn't
              render for auction deal collaborations (no campaignId). */}
          {collaboration.offerDetails?.license_extensions?.length > 0 && (
            <div className="border-t border-border pt-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Licensing
              </h4>
              <p className="text-xs text-muted-foreground">
                {collaboration.offerDetails.license_extensions.length} extension
                {collaboration.offerDetails.license_extensions.length > 1 ? 's' : ''} · $
                {new Intl.NumberFormat('en-US').format(
                  collaboration.offerDetails.license_extensions.reduce(
                    (sum: number, e: { price?: number }) => sum + (Number(e.price) || 0),
                    0,
                  ),
                )}{' '}
                at the athlete's listed price, invoiced separately
              </p>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  )
}
